import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { useUiTitle } from '../hooks/useUiTitle';
import GradientBlobBackground from '../components/GradientBlobBackground/GradientBlobBackground';
import KineticText from '../components/KineticText/KineticText';
import logo from '../assets/logo.png';

// Same dark-purple treatment as Login.tsx, so logging out doesn't drop back
// into the dashboard's plain light/dark theme mid-flow.
const ACCENT = '#8b5cf6';

const Logout: React.FC = () => {
  const navigate = useNavigate();
  const uiTitle = useUiTitle();

  useEffect(() => {
    document.title = uiTitle;
  }, [uiTitle]);

  useEffect(() => {
    const timer = setTimeout(() => {
      navigate('/login');
    }, 5000);

    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <div className="relative z-0 min-h-screen w-full flex flex-col" style={{ background: '#07070b', color: '#f5f5f7' }}>
      <div className="absolute inset-0 -z-10">
        <GradientBlobBackground accent={ACCENT} />
      </div>

      <header className="relative z-10 flex items-center gap-4 flex-wrap px-6 py-7 max-w-[1280px] mx-auto">
        <Link to="/welcome" className="flex items-center gap-2.5 transition-colors hover:text-white" style={{ color: '#f5f5f7' }}>
          <img src={logo} alt="" className="h-8 w-8 brightness-0 invert" />
          <KineticText text={uiTitle} as="span" className="text-[15px] tracking-[0.2px]" />
        </Link>
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center p-6">
        <div
          className="w-full max-w-[420px] rounded-[20px] p-9 box-border text-center"
          style={{
            background: 'rgba(18,18,24,0.72)',
            border: '1px solid rgba(255,255,255,0.08)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 24px 60px rgba(0,0,0,0.5)'
          }}
        >
          <div className="flex justify-center mb-5">
            <CheckCircleIcon className="h-14 w-14" style={{ color: '#34d399' }} />
          </div>

          <h1 className="m-0 mb-2.5 text-2xl font-semibold leading-snug">
            Successfully <span style={{ color: '#c4b5fd' }}>Logged Out</span>
          </h1>
          <p className="m-0 mb-6 text-sm leading-relaxed" style={{ color: '#9ba0ad' }}>
            You have been logged out from all sessions.
          </p>

          <div
            className="text-left rounded-xl px-4 py-3.5 mb-6 text-sm leading-relaxed"
            style={{ background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.22)', color: '#d8d4e6' }}
          >
            Your session has been terminated and you've been logged out from the identity provider.
          </div>

          <button
            onClick={() => navigate('/login')}
            className="w-full text-white font-semibold rounded-[11px] px-4 py-3 text-[14.5px] transition-transform hover:-translate-y-px"
            style={{ background: `linear-gradient(135deg,${ACCENT},#7c3aed)` }}
          >
            Return to Login
          </button>

          <p className="mt-5 text-xs" style={{ color: '#6b7280' }}>
            Redirecting to login in 5 seconds...
          </p>
        </div>
      </main>

      <footer className="relative z-10 text-center py-5 text-xs" style={{ color: '#6b7280' }}>
        <span>{uiTitle} · Secure Access Management</span>
      </footer>
    </div>
  );
};

export default Logout;
